// Procedural Beat & Soundpack Synthesizer for LocalTurntables
// Creates pristine, punchy loops and classic one-shot DJ samples

export function generateProceduralBeats(audioCtx) {
  // 1. Classic Hip-Hop Boom Bap (90 BPM)
  const hiphopBuffer = createBeatBuffer(audioCtx, 90, 4, (beat, time, step, totalSteps, setKick, setSnare, setHihat) => {
    if (step === 0 || step === 6 || step === 10) setKick(0.95);
    if (step === 4 || step === 12) setSnare(0.85);
    if (step % 2 === 0) setHihat(0.6, step % 4 === 2 ? 0.35 : 0.5);
    if (step === 15) setKick(0.5);
  });

  // 2. Deep House / Club Groove (124 BPM)
  const houseBuffer = createBeatBuffer(audioCtx, 124, 4, (beat, time, step, totalSteps, setKick, setSnare, setHihat, setClap) => {
    if (step % 4 === 0) setKick(1.0);
    if (step % 4 === 2) setHihat(0.7, 0.4);
    if (step === 4 || step === 12) setClap(0.8);
    if (step === 14) setHihat(0.3, 0.1);
  });

  // 3. Peak Time Techno (132 BPM)
  const technoBuffer = createBeatBuffer(audioCtx, 132, 4, (beat, time, step, totalSteps, setKick, setSnare, setHihat, setClap, setRumble) => {
    if (step % 4 === 0) {
      setKick(1.0);
      setRumble(0.6);
    }
    if (step % 2 === 1) setHihat(0.5, 0.2);
    if (step % 4 === 2) setHihat(0.8, 0.5);
    if (step === 4 || step === 12) setClap(0.75);
  });

  // 4. Liquid Drum & Bass Breakbeat (174 BPM)
  const dnbBuffer = createBeatBuffer(audioCtx, 174, 4, (beat, time, step, totalSteps, setKick, setSnare, setHihat) => {
    if (step === 0 || step === 10) setKick(1.0);
    if (step === 4 || step === 12) setSnare(0.9);
    if (step === 15) setSnare(0.5);
    setHihat(0.5, 0.15);
  });

  // 5. UK Garage 2-Step (130 BPM)
  const ukgBuffer = createBeatBuffer(audioCtx, 130, 4, (beat, time, step, totalSteps, setKick, setSnare, setHihat, setClap, setRumble) => {
    if (step === 0 || step === 7 || step === 10) setKick(0.95);
    if (step === 4 || step === 12) setSnare(0.85);
    if (step % 2 === 0) setHihat(0.4, 0.1);
    if (step === 3 || step === 11 || step === 15) setHihat(0.6, 0.3);
    if (step === 6 || step === 14) setRumble(0.4);
  });

  // 6. Irish Bodhrán & Celtic Reel Break (116 BPM)
  const tradBuffer = createBeatBuffer(audioCtx, 116, 4, (beat, time, step, totalSteps, setKick, setSnare, setHihat, setClap, setRumble) => {
    if (step % 3 === 0) setKick(0.85);
    if (step % 3 === 1) setHihat(0.5, 0.2);
    if (step % 3 === 2) setSnare(0.6);
  });

  // 7. Scratch Cue Tone & Crackle Bed (100 BPM)
  const scratchSampleBuffer = createToneAndNoiseBuffer(audioCtx, 4);

  return [
    { id: 'beat_hiphop', name: 'Boom Bap Groove (90 BPM)', artist: 'LocalFilm Beats', bpm: 90, duration: '0:10', buffer: hiphopBuffer },
    { id: 'beat_house', name: 'Deep House Club (124 BPM)', artist: 'Dublin Sound Lab', bpm: 124, duration: '0:07', buffer: houseBuffer },
    { id: 'beat_techno', name: 'Peak Techno Raw (132 BPM)', artist: 'Wicklow Tech', bpm: 132, duration: '0:07', buffer: technoBuffer },
    { id: 'beat_ukg', name: 'UKG 2-Step Swing (130 BPM)', artist: 'LocalTurntables', bpm: 130, duration: '0:07', buffer: ukgBuffer },
    { id: 'beat_trad', name: 'Bodhrán Celtic Reel (116 BPM)', artist: 'Irish Folk Stems', bpm: 116, duration: '0:08', buffer: tradBuffer },
    { id: 'beat_dnb', name: 'Liquid DnB Break (174 BPM)', artist: 'Sub-Heavy Ireland', bpm: 174, duration: '0:05', buffer: dnbBuffer },
    { id: 'beat_scratch_fx', name: 'DJ Scratch Vocal Tone & Vinyl Bed', artist: 'Turntablist Essentials', bpm: 100, duration: '0:04', buffer: scratchSampleBuffer }
  ];
}

// Procedural One-Shot DJ Sound FX Generator
export function generateOneShotSFX(audioCtx) {
  const sampleRate = audioCtx.sampleRate;

  // 1. Airhorn Sound FX (Dual square wave blast)
  const airhorn = (() => {
    const dur = 0.9;
    const len = Math.floor(dur * sampleRate);
    const buf = audioCtx.createBuffer(2, len, sampleRate);
    const l = buf.getChannelData(0);
    const r = buf.getChannelData(1);
    for (let i = 0; i < len; i++) {
      const t = i / sampleRate;
      const env = Math.min(1, t * 50) * Math.max(0, 1 - t / dur);
      const tone1 = Math.sin(2 * Math.PI * 466.16 * t) > 0 ? 0.3 : -0.3; // Bb4
      const tone2 = Math.sin(2 * Math.PI * 587.33 * t) > 0 ? 0.25 : -0.25; // D5
      const tone3 = Math.sin(2 * Math.PI * 698.46 * t) > 0 ? 0.25 : -0.25; // F5
      const s = (tone1 + tone2 + tone3) * env * 0.7;
      l[i] = s;
      r[i] = s;
    }
    return buf;
  })();

  // 2. Vinyl Rewind / Backspin Effect
  const rewind = (() => {
    const dur = 0.8;
    const len = Math.floor(dur * sampleRate);
    const buf = audioCtx.createBuffer(2, len, sampleRate);
    const l = buf.getChannelData(0);
    const r = buf.getChannelData(1);
    for (let i = 0; i < len; i++) {
      const t = i / sampleRate;
      const progress = t / dur;
      const freq = 120 + 2400 * Math.pow(1 - progress, 2);
      const env = Math.exp(-t * 2);
      const noise = (Math.random() * 2 - 1) * 0.4;
      const tone = Math.sin(2 * Math.PI * freq * t) * 0.6;
      const s = (tone + noise) * env * 0.75;
      l[i] = s;
      r[i] = s * 0.95;
    }
    return buf;
  })();

  // 3. Dub Siren Sweep
  const dubSiren = (() => {
    const dur = 1.2;
    const len = Math.floor(dur * sampleRate);
    const buf = audioCtx.createBuffer(2, len, sampleRate);
    const l = buf.getChannelData(0);
    const r = buf.getChannelData(1);
    for (let i = 0; i < len; i++) {
      const t = i / sampleRate;
      const mod = Math.sin(2 * Math.PI * 6 * t);
      const freq = 650 + 400 * mod;
      const env = Math.min(1, t * 20) * (1 - t / dur);
      const s = (Math.sin(2 * Math.PI * freq * t) > 0 ? 0.4 : -0.4) * env;
      l[i] = s;
      r[i] = s;
    }
    return buf;
  })();

  // 4. Laser Drop FX
  const laser = (() => {
    const dur = 0.45;
    const len = Math.floor(dur * sampleRate);
    const buf = audioCtx.createBuffer(2, len, sampleRate);
    const l = buf.getChannelData(0);
    const r = buf.getChannelData(1);
    for (let i = 0; i < len; i++) {
      const t = i / sampleRate;
      const freq = 2800 * Math.exp(-t * 18);
      const env = Math.exp(-t * 7);
      const s = Math.sin(2 * Math.PI * freq * t) * env * 0.8;
      l[i] = s;
      r[i] = s;
    }
    return buf;
  })();

  return { airhorn, rewind, dubSiren, laser };
}

function createBeatBuffer(audioCtx, bpm, bars, patternFn) {
  const sampleRate = audioCtx.sampleRate;
  const beatsPerSecond = bpm / 60;
  const secondsPerBeat = 1 / beatsPerSecond;
  const totalBeats = bars * 4;
  const totalSeconds = totalBeats * secondsPerBeat;
  const numSamples = Math.floor(totalSeconds * sampleRate);

  const buffer = audioCtx.createBuffer(2, numSamples, sampleRate);
  const left = buffer.getChannelData(0);
  const right = buffer.getChannelData(1);

  const stepsPerBeat = 4;
  const totalSteps = totalBeats * stepsPerBeat;
  const secondsPerStep = secondsPerBeat / stepsPerBeat;

  for (let step = 0; step < totalSteps; step++) {
    const stepStartTime = step * secondsPerStep;
    const startSample = Math.floor(stepStartTime * sampleRate);

    const setKick = (gain = 1.0) => renderKick(left, right, startSample, sampleRate, gain);
    const setSnare = (gain = 0.8) => renderSnare(left, right, startSample, sampleRate, gain);
    const setHihat = (gain = 0.5, decay = 0.1) => renderHihat(left, right, startSample, sampleRate, gain, decay);
    const setClap = (gain = 0.7) => renderClap(left, right, startSample, sampleRate, gain);
    const setRumble = (gain = 0.5) => renderSubBass(left, right, startSample, sampleRate, gain, secondsPerStep * 2);

    patternFn(step / stepsPerBeat, stepStartTime, step, totalSteps, setKick, setSnare, setHihat, setClap, setRumble);
  }

  for (let i = 0; i < numSamples; i++) {
    left[i] = Math.tanh(left[i] * 1.15) * 0.95;
    right[i] = Math.tanh(right[i] * 1.15) * 0.95;
  }

  return buffer;
}

function renderKick(left, right, startIdx, sampleRate, gain) {
  const duration = 0.35;
  const len = Math.floor(duration * sampleRate);
  for (let i = 0; i < len; i++) {
    const idx = startIdx + i;
    if (idx >= left.length) break;
    const t = i / sampleRate;
    const freq = 42 + 110 * Math.exp(-t * 28);
    const env = Math.exp(-t * 12) * gain;
    const sample = Math.sin(2 * Math.PI * freq * t) * env;
    const click = (Math.random() * 2 - 1) * Math.exp(-t * 200) * 0.35 * gain;
    left[idx] += sample + click;
    right[idx] += sample + click;
  }
}

function renderSnare(left, right, startIdx, sampleRate, gain) {
  const duration = 0.28;
  const len = Math.floor(duration * sampleRate);
  for (let i = 0; i < len; i++) {
    const idx = startIdx + i;
    if (idx >= left.length) break;
    const t = i / sampleRate;
    const bodyEnv = Math.exp(-t * 22) * 0.5 * gain;
    const tone = Math.sin(2 * Math.PI * 185 * t) * bodyEnv;
    const noiseEnv = Math.exp(-t * 14) * 0.65 * gain;
    const noise = (Math.random() * 2 - 1) * noiseEnv;
    left[idx] += (tone + noise) * 0.9;
    right[idx] += (tone + noise * 1.05) * 0.9;
  }
}

function renderHihat(left, right, startIdx, sampleRate, gain, decay) {
  const duration = decay;
  const len = Math.floor(duration * sampleRate);
  for (let i = 0; i < len; i++) {
    const idx = startIdx + i;
    if (idx >= left.length) break;
    const t = i / sampleRate;
    const env = Math.exp(-t / (decay * 0.25)) * gain;
    const metallic = (Math.sin(2 * Math.PI * 7200 * t) + Math.sin(2 * Math.PI * 9400 * t)) * 0.3;
    const noise = (Math.random() * 2 - 1) * 0.7;
    const sample = (metallic + noise) * env * 0.45;
    left[idx] += sample;
    right[idx] += sample;
  }
}

function renderClap(left, right, startIdx, sampleRate, gain) {
  const duration = 0.22;
  const len = Math.floor(duration * sampleRate);
  for (let i = 0; i < len; i++) {
    const idx = startIdx + i;
    if (idx >= left.length) break;
    const t = i / sampleRate;
    let burst = 0;
    if (t < 0.012) burst = (Math.random() * 2 - 1) * 0.4;
    else if (t < 0.024) burst = (Math.random() * 2 - 1) * 0.6;
    else if (t < 0.036) burst = (Math.random() * 2 - 1) * 0.8;
    else {
      const tailEnv = Math.exp(-(t - 0.036) * 18);
      burst = (Math.random() * 2 - 1) * tailEnv;
    }
    const sample = burst * gain * 0.75;
    left[idx] += sample;
    right[idx] += sample;
  }
}

function renderSubBass(left, right, startIdx, sampleRate, gain, duration) {
  const len = Math.floor(duration * sampleRate);
  for (let i = 0; i < len; i++) {
    const idx = startIdx + i;
    if (idx >= left.length) break;
    const t = i / sampleRate;
    const env = Math.exp(-t * 3.5) * gain;
    const sample = Math.sin(2 * Math.PI * 48 * t) * env * 0.5;
    left[idx] += sample;
    right[idx] += sample;
  }
}

function createToneAndNoiseBuffer(audioCtx, seconds) {
  const sampleRate = audioCtx.sampleRate;
  const numSamples = Math.floor(seconds * sampleRate);
  const buffer = audioCtx.createBuffer(2, numSamples, sampleRate);
  const left = buffer.getChannelData(0);
  const right = buffer.getChannelData(1);

  for (let i = 0; i < numSamples; i++) {
    const t = i / sampleRate;
    const tone = Math.sin(2 * Math.PI * (800 + 400 * Math.sin(t * 4)) * t) * 0.35;
    const crackle = (Math.random() > 0.994 ? (Math.random() * 2 - 1) * 0.25 : 0);
    const noise = (Math.random() * 2 - 1) * 0.015;
    const sample = tone + crackle + noise;
    left[i] = sample;
    right[i] = sample;
  }
  return buffer;
}
